import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import Button from "@mui/material/Button";
import {
    type Beneficiary,
    ChangesDisabledReason,
    type GetLifeDetailResponse,
    InsuredPersonRole,
    type PensionBeneficiary,
    ProductType,
} from "@repo/api";
import { useAllowedChanges } from "@repo/logic";
import { cs } from "@repo/ui";
import { type FC } from "react";
import { useContractId } from "../../context/ContractIDContext";
import { useContractTypeContext } from "../../context/ContractTypeContext";
import { useLifeDetail } from "../../queryHooks";
import { useDrawer } from "../Drawer/context";
import InfoIconDrawer from "../Drawer/InfoIconDrawer";
import { useSelectedPerson } from "../ZivotniPojisteni/Tabs/components/Persons/SelectedPersonContext";
import BeneficiariesByMail from "./BeneficiariesByMail";
import BeneficiariesChangeForm from "./BeneficiariesChangeForm";
import BeneficiariesNotEditable from "./BeneficiariesNotEditable";
import BeneficiaryChangeInProgress from "./BeneficiaryChangeInProgress";
import HasNoBeneficiaries from "./HasNoBeneficiaries";

const messages: Record<ChangesDisabledReason, JSX.Element> = {
    [ChangesDisabledReason.BeneficiaryChangeInProgress]: <BeneficiaryChangeInProgress />,
    [ChangesDisabledReason.BeneficiaryIsABank]: <BeneficiariesByMail />,
    [ChangesDisabledReason.HasNoBeneficiaries]: <HasNoBeneficiaries />,
    [ChangesDisabledReason.FutureEmployerOrClientContributionChange]: <BeneficiaryChangeInProgress />,
    [ChangesDisabledReason.None]: <BeneficiariesByMail />,
};

const ReasonToMessage: FC<{
    reasons: ChangesDisabledReason[];
    title: string;
    customReason?: string;
}> = ({ reasons, customReason, title }) => {
    const description = customReason ?? (reasons.length > 0 ? reasons.map((reason) => messages[reason]) : cs.global.changeIsBlocked);

    return (
        <InfoIconDrawer description={<>{description}</>} title={title} />
    );
};

type ChangeBeneficiariesButtonProps = {
    beneficiaries: PensionBeneficiary[] | Beneficiary[];
    editable?: boolean;
    isLoading: boolean;
    onSuccess: () => Promise<void>;
    onError: () => Promise<void>;
    canChangeBeneficiaries?: boolean | null;
    disabledReasons?: ChangesDisabledReason[] | null;
    customDisabledReason?: string;
};

const ChangeBeneficiariesButton: FC<ChangeBeneficiariesButtonProps> = ({
    beneficiaries,
    onSuccess,
    onError,
    editable = true,
    canChangeBeneficiaries,
    disabledReasons,
    isLoading,
    customDisabledReason,
    ...rest
}) => {
    const { showDrawer, setDrawerContent } = useDrawer();
    const { contractType } = useContractTypeContext();
    const { contractId: id } = useContractId();
    const ai = useAppInsightsContext();

    const title =
        beneficiaries.length > 0
            ? cs.product.beneficiaries.changeBeneficiaries[contractType]
            : cs.product.beneficiaries.addBeneficiar[contractType];
    const canOpenBeneficiariesChange = canChangeBeneficiaries === true;
    const reasons = disabledReasons ?? [];

    const modalContent = (() => {
        if (isLoading) return undefined;
        if (editable) {
            return canOpenBeneficiariesChange ? (
                <BeneficiariesChangeForm
                    beneficiaries={beneficiaries}
                    onSuccess={onSuccess}
                    onError={onError}
                    contractType={contractType}
                    id={id}
                />
            ) : (
                <ReasonToMessage reasons={reasons} customReason={customDisabledReason} title={title} />
            );
        }
        return <BeneficiariesNotEditable title={title} />;
    })();

    return (
        <Button
            data-test="add-beneficiary-button"
            {...rest}
            variant="contained"
            sx={{
                alignSelf: { md: "flex-start" },
                width: { md: "auto", xs: "100%" },
            }}
            onClick={() => {
                if (isLoading || !modalContent) {
                    return;
                }
                ai.trackEvent({
                    name: `${cs.product.beneficiaries.changeBeneficiaries[contractType]} [dialog]`,
                });
                showDrawer();
                setDrawerContent(modalContent);
            }}
            disabled={isLoading}
        >
            {title}
        </Button>
    );
};

export default ChangeBeneficiariesButton;

const ChangeBeneficiariesButtonWithLifeData = () => {
    const { contractId } = useContractId();
    const { data, refetch, isSuccess } = useLifeDetail<GetLifeDetailResponse>({
        id: contractId,
    });
    const { data: allowedChangesData, isSuccess: allowedChangesSuccess } = useAllowedChanges({
        id: contractId,
        options: { enabled: Boolean(contractId) },
    });
    const { selectedPersonIndex } = useSelectedPerson();
    const insuredPerson = isSuccess ? data.insuredPersons[selectedPersonIndex] : undefined;
    const isAllowedChangesLoaded = allowedChangesSuccess === true;
    const customDisabledReason =
        isAllowedChangesLoaded &&
        allowedChangesData.canChangeBeneficiaries !== true &&
        allowedChangesData.beneficiariesDescription
            ? allowedChangesData.beneficiariesDescription
            : undefined;

    return (
        <ChangeBeneficiariesButton
            beneficiaries={insuredPerson ? insuredPerson.beneficiaries : []}
            editable={(
                [
                    InsuredPersonRole.PolicyHolder,
                    InsuredPersonRole.PolicyHolderAndFirst,
                    InsuredPersonRole.PolicyHolderAndSecond,
                ] as string[]
            ).includes(insuredPerson?.role as string)}
            isLoading={!isSuccess || !isAllowedChangesLoaded}
            onError={async () => {
                await refetch();
            }}
            onSuccess={async () => {
                await refetch();
            }}
            canChangeBeneficiaries={allowedChangesData?.canChangeBeneficiaries}
            disabledReasons={allowedChangesData?.reasons}
            customDisabledReason={customDisabledReason}
        />
    );
};

export const ChangeBeneficiariesButtonByLifeContractType: FC = () => {
    const { contractType } = useContractTypeContext();
    if (contractType !== ProductType.Cl) {
        throw new Error(`Wrong contract type in ChangeBeneficiariesButtonByLifeContractType: ${contractType}`);
    }
    return <ChangeBeneficiariesButtonWithLifeData />;
};
